// @file helper functions related to posts/wishes

import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { __ } from '@@/bits/intl'
import type { AttachmentProps, PostAttributes, WishAttachment } from '@@/types'
import type { Tweet, TweetDisplayAttributes } from '@@/types/surface'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { BeethovenLuminance } from '@padlet/beethoven-client/src/types'

function isTweet(attachmentProps: AttachmentProps | null | undefined): boolean {
  return attachmentProps?.type?.toLowerCase() === 'tweet'
}

function isArcade(attachmentProps: AttachmentProps | null | undefined): boolean {
  return attachmentProps?.type?.toLowerCase() === 'arcade'
}

function getTweet(attachmentProps: AttachmentProps | null | undefined): Tweet {
  return {
    canDisplay: attachmentProps?.can_display ?? true,
    text: attachmentProps?.text,

    author: attachmentProps?.author?.name || attachmentProps?.author?.username,
    createdAt: attachmentProps?.created_at,
    attachments: attachmentProps?.attachments,
    entities: attachmentProps?.entities,
  }
}

function getTweetDisplayAttributes(
  tweet: Tweet,
  options: { monthFormat: 'short' | 'long' } = { monthFormat: 'long' },
): TweetDisplayAttributes {
  const canDisplay = tweet.canDisplay
  if (!canDisplay) {
    return {
      text: __('Tweet cannot be displayed'),
      author: tweet.author,
    }
  }

  const date = new Date(tweet.createdAt)
  const createdAtDateString = date.toLocaleString(getHyphenatedCurrentLocale(), {
    year: 'numeric',
    month: options.monthFormat,
    day: 'numeric',
  })
  return {
    text: tweet.text,
    author: tweet.author,
    createdAtDateString,
  }
}

// Arcade posts bypass Beethoven entirely and render `arcade_thumbnail_url` directly. We synthesize a
// `BeethovenDisplayAttributes` so consumers expecting Beethoven data (e.g. WhiteboardAttachment's corner
// action button) keep working. Mirrors `getTweetDisplayAttributes`, which does the same for tweets.
function getArcadeDisplayAttributes(post: PostAttributes): BeethovenDisplayAttributes {
  const attachmentProps = post.wish_content?.attachment_props
  return {
    url: (attachmentProps?.arcade_game_url as string | undefined) ?? '',
    content_category: 'page',
    content_subcategory: 'arcade',
    image_url: (attachmentProps?.arcade_thumbnail_url as string | undefined) ?? '',
    image_width: 0,
    image_height: 0,
    image_aspect_ratio: 1.5,
    image_color: '',
    image_luminance: BeethovenLuminance.dark,
    extension: '',
    embed_code: '',
    title: (attachmentProps?.arcade_game_title as string | undefined) ?? '',
    description: '',
    provider_image_url: '',
    provider_name: 'arcade',
    provider_image_is_fallback: true,
    metadata: null,
    show_meta: false,
    is_tiny: false,
    is_fallback: false,
    is_downloadable: false,
    is_transparent: false,
  } satisfies BeethovenDisplayAttributes
}

function isPhotoAlbum(post?: PostAttributes | null): post is PostAttributes & { attachments: WishAttachment[] } {
  return post?.attachments != null && post.attachments.length > 1
}

function hasRegularAttachment(post?: PostAttributes | null): boolean {
  return !!post?.attachment
}

function hasRichAttachment(post?: PostAttributes | null): boolean {
  return post?.wish_content?.attachment_props?.type != null
}

function hasAnyAttachments(post?: PostAttributes | null): boolean {
  return hasRegularAttachment(post) || hasRichAttachment(post) || isPhotoAlbum(post)
}

function hasResolvableAttachmentUrl(post?: PostAttributes | null): boolean {
  if (isPhotoAlbum(post)) return true
  return !!(post?.wish_content?.attachment_props?.url || post?.attachment)
}

/**
 * Classifies a single post as a "photo post": a multi-image album (always images),
 * or a single non-rich attachment whose display attributes are a photo.
 * Returns false when display attributes are not yet resolved, so callers gating on
 * this stay fail-safe (never treat an unclassified attachment as a photo).
 */
function isPhotoPost(post?: PostAttributes | null, displayAttributes?: BeethovenDisplayAttributes | null): boolean {
  if (post == null) return false
  if (isPhotoAlbum(post)) return true
  if (!hasRegularAttachment(post)) return false
  if (hasRichAttachment(post)) return false
  return displayAttributes?.content_category === 'photo'
}

type PostInteraction = 'expand' | 'link' | 'download' | 'padlet' | 'play'

const COMMON_IMAGE_FILE_EXTENSIONS = [
  // Common image file types based on MDN Web Docs
  // https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Image_types#common_image_file_types
  '.apng',
  '.avif',
  '.gif',
  '.jpg',
  '.jpeg',
  '.jfif',
  '.pjpeg',
  '.pjp',
  '.png',
  '.svg',
  '.webp',
  // iOS
  '.heic',
  '.heif',
] as const

/**
 * Checks if a file has a common image file extension
 * @param file - The file to check
 * @returns true if the file has an image extension, false otherwise
 */
function isFileWithImageFileExtension(file: File): boolean {
  const fileName = file.name.toLowerCase()
  return COMMON_IMAGE_FILE_EXTENSIONS.some((ext) => fileName.endsWith(ext))
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}
export {
  isTweet,
  isArcade,
  getTweet,
  getTweetDisplayAttributes,
  getArcadeDisplayAttributes,
  isPhotoAlbum,
  isPhotoPost,
  hasRegularAttachment,
  hasRichAttachment,
  hasAnyAttachments,
  hasResolvableAttachmentUrl,
  isFileWithImageFileExtension,
  isImageFile,
  COMMON_IMAGE_FILE_EXTENSIONS,
}
export type { PostInteraction }
